import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * Workspace store for managing selected workspace state
 *
 * Uses Zustand persist middleware to automatically sync to sessionStorage.
 * This provides a single source of truth with automatic hydration on page load.
 *
 * NOTE: The persisted value is validated against the user's actual workspaces
 * in useWorkspaceResolver to handle stale data (e.g., different user, deleted workspace).
 */

interface WorkspaceState {
  selectedWorkspaceId: string | null;
  setSelectedWorkspaceId: (workspaceId: string | null) => void;
  clearSelectedWorkspace: () => void;
}

/**
 * Workspace store with sessionStorage persistence
 *
 * Key features:
 * - Automatic persistence to sessionStorage
 * - Automatic hydration on page load
 * - Validation against API in useWorkspaceResolver
 */
export const useWorkspaceStore = create<WorkspaceState>()(
  persist(
    (set) => ({
      selectedWorkspaceId: null,

      setSelectedWorkspaceId: (workspaceId) =>
        set({ selectedWorkspaceId: workspaceId }),

      clearSelectedWorkspace: () =>
        set({ selectedWorkspaceId: null }),
    }),
    {
      name: 'workspace-storage',
      storage: createJSONStorage(() => sessionStorage),
    }
  )
);
